import React, { useState, useEffect, useCallback } from 'react';
import PanelLayout from '../Panel/PanelLayout';
import { bpAPI } from '../../services/api';
import { Search } from 'lucide-react';

const C = { surface: '#FFFFFF', border: '#e8e8f1', borderSoft: '#f0f0f6', ink: '#14142b', muted: '#6e6e86', primary: '#7c3aed', success: '#10B981', warning: '#F59E0B', danger: '#EF4444', radius: 14, radiusSm: 8 };

const StatusBadge = ({ status }) => {
  const map = {
    success: { bg: '#D1FAE5', color: '#059669' },
    failed:  { bg: '#FEE2E2', color: '#DC2626' },
    pending: { bg: '#FEF3C7', color: '#D97706' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20 }}>
      {status}
    </span>
  );
};

const BPTransactions = () => {
  const [txns, setTxns]       = useState([]);
  const [meta, setMeta]       = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('');

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await bpAPI.transactions({ search, status, page });
      setTxns(res.data.data);
      setMeta(res.data.meta || {});
    } catch {}
    finally { setLoading(false); }
  }, [search, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <PanelLayout role="business_partner" title="Transactions" breadcrumb="Dashboard / Transactions">
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.ink, letterSpacing: '-.02em' }}>Transactions</h1>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>All transactions from your referred merchants.</p>
            </div>
          </div>
          <div style={{ background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radius, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            <div style={{ position: 'relative', flex: 1, borderRight: `1px solid ${C.borderSoft}` }}>
              <Search size={14} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none' }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by txn ID or order ID…"
                style={{ width: '100%', padding: '12px 14px 12px 38px', border: 'none', outline: 'none', fontSize: 13, background: 'transparent', color: C.ink, fontFamily: 'inherit', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', padding: '0 12px', gap: 4 }}>
              {[{ label: 'All', val: '', color: C.primary }, { label: 'Success', val: 'success', color: C.success }, { label: 'Pending', val: 'pending', color: C.warning }, { label: 'Failed', val: 'failed', color: C.danger }].map(t => (
                <button key={t.val} onClick={() => setStatus(t.val)} style={{ height: 30, padding: '0 12px', borderRadius: 999, border: `1.5px solid ${status === t.val ? t.color : 'transparent'}`, background: status === t.val ? t.color : 'transparent', color: status === t.val ? '#fff' : C.muted, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit', transition: 'all .15s' }}>{t.label}</button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ border: '1px solid #e8e8f1', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1.5fr 1.2fr 100px 100px 130px',
            background: '#f9f9fc', padding: '10px 16px',
            fontSize: 10, fontWeight: 700, color: '#4a4a6a', letterSpacing: 1, textTransform: 'uppercase',
          }}>
            <span>Transaction ID</span><span>Order ID</span><span>Amount</span><span>Status</span><span>Date</span>
          </div>

          {loading && <div style={{ textAlign: 'center', padding: 60, color: '#6e6e86' }}>Loading…</div>}

          {!loading && txns.length === 0 && (
            <div style={{ textAlign: 'center', padding: 60, color: '#6e6e86', fontSize: 13 }}>No transactions found.</div>
          )}

          {!loading && txns.map((t, i) => (
            <div key={t.id} style={{
              display: 'grid', gridTemplateColumns: '1.5fr 1.2fr 100px 100px 130px',
              padding: '13px 16px', alignItems: 'center',
              borderTop: '1px solid #f0f0f6',
              background: i % 2 === 0 ? '#fff' : '#FAFAFA',
            }}>
              <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#14142b', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t.transaction_id}
              </div>
              <div style={{ fontSize: 12, color: '#4a4a6a', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t.merchant_order_id || '—'}
              </div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#14142b' }}>
                ₹{Number(t.amount || 0).toLocaleString('en-IN')}
              </div>
              <StatusBadge status={t.status} />
              <div style={{ fontSize: 12, color: '#6e6e86' }}>
                {t.created_at ? new Date(t.created_at).toLocaleDateString('en-IN') : '—'}
              </div>
            </div>
          ))}
        </div>

        {meta.last_page > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
            {Array.from({ length: meta.last_page }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => load(page)}
                style={{
                  width: 36, height: 36, borderRadius: 8, border: '1px solid #e8e8f1',
                  background: page === meta.current_page ? '#14142b' : '#fff',
                  color: page === meta.current_page ? '#fff' : '#14142b',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >{page}</button>
            ))}
          </div>
        )}
      </div>
    </PanelLayout>
  );
};

export default BPTransactions;
